import { createContext, useContext, useState, useEffect, useMemo } from 'react';
import api from '@app/services/api';

const AuthContext = createContext(null);

// Decode a JWT payload without verifying (verification is server-side).
// Used only to read the `act` claim so the UI can tell it is inside an
// admin impersonation session.
function decodeTokenPayload(token) {
  try {
    const base64 = token.split('.')[1].replace(/-/g, '+').replace(/_/g, '/');
    return JSON.parse(atob(base64));
  } catch {
    return null;
  }
}

// sessionStorage key holding the admin's own token while they are
// impersonating another user, so we can restore it on return/expiry.
const IMPERSONATOR_TOKEN_KEY = 'zebra_impersonator_token';

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(() => localStorage.getItem('token'));
  const [loading, setLoading] = useState(true);
  const [userGroups, setUserGroups] = useState(null);

  const fetchUserGroups = async () => {
    try {
      const groups = await api.getUserGroups();
      setUserGroups((groups || []).map(g => g.name));
    } catch (err) {
      console.error('Failed to fetch user groups:', err);
      setUserGroups([]);
    }
  };

  useEffect(() => {
    const boot = (token, isRetryAsImpersonator) => {
      api.setToken(token);
      api.getMe()
        .then((userData) => {
          setToken(token);
          setUser(userData);
          fetchUserGroups();
          setLoading(false);
        })
        .catch(() => {
          localStorage.removeItem('token');
          // Reload while impersonating a since-deleted/deactivated user:
          // the impersonation token 401s on /me, but the admin's own
          // token is stashed — restore it instead of dumping the admin
          // to the sign-in screen. One retry only.
          const stashed = !isRetryAsImpersonator && sessionStorage.getItem(IMPERSONATOR_TOKEN_KEY);
          if (stashed) {
            sessionStorage.removeItem(IMPERSONATOR_TOKEN_KEY);
            localStorage.setItem('token', stashed);
            boot(stashed, true);
            return;
          }
          setLoading(false);
        });
    };
    const token = localStorage.getItem('token');
    if (token) {
      boot(token, false);
    } else {
      setLoading(false);
    }
  }, []);

  const login = (newToken) => {
    localStorage.setItem('token', newToken);
    api.setToken(newToken);
    setToken(newToken);
    return api.getMe().then((userData) => {
      setUser(userData);
      fetchUserGroups();
      return userData;
    });
  };

  // Re-read /me after the person edits their own profile (display name,
  // avatar) so the account menu and every other consumer of `user`
  // repaint from the mirror row, not the 7-day JWT's mint-time claims.
  const refreshUser = () => api.getMe().then((userData) => {
    setUser(userData);
    return userData;
  });

  const logout = () => {
    localStorage.removeItem('token');
    api.setToken(null);
    setUser(null);
    setToken(null);
    setUserGroups(null);
  };

  // The `act` claim of the current token, when present, means this
  // session is an admin impersonating another user. null otherwise.
  const impersonation = useMemo(() => decodeTokenPayload(token)?.act || null, [token]);

  // Enter an impersonation session: stash the admin's own token so it
  // can be restored later, then swap in the impersonation token (login
  // refreshes me + groups off the new identity).
  const startImpersonation = (newToken) => {
    sessionStorage.setItem(IMPERSONATOR_TOKEN_KEY, localStorage.getItem('token') || '');
    return login(newToken);
  };

  // Leave an impersonation session: best-effort audit ping, then
  // restore the admin's own token (or fully log out if it is gone).
  // Navigation is handled by the caller.
  const endImpersonation = async () => {
    const real = sessionStorage.getItem(IMPERSONATOR_TOKEN_KEY);
    // Await the audit-end WHILE the impersonation token is still active,
    // so the server sees its act claim and writes the impersonation_ended
    // row. A fire-and-forget call races login(real) and reaches the
    // server as the restored admin (no act) -> no audit row.
    try { await api.impersonationEnd(); } catch { /* best-effort audit */ }
    sessionStorage.removeItem(IMPERSONATOR_TOKEN_KEY);
    if (real) {
      return login(real);
    }
    logout();
    return Promise.resolve();
  };

  return (
    <AuthContext.Provider value={{
      user,
      token,
      loading,
      login,
      logout,
      refreshUser,
      userGroups,
      impersonation,
      startImpersonation,
      endImpersonation
    }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}
